import { useQuery } from '@tanstack/react-query'
import React, { useEffect, useRef, useState } from 'react'
import { BiQrScan } from 'react-icons/bi'

import { ddPanelMainAccountAPI } from '../../../api/services/dd_panel_main_accounts'
import { SubAccount } from '../../../api/services/dd_panel_sub_accounts/types.ts'
import { useAuth } from '../../../context/auth'
import { setSubAccountsTableColumns } from '../../../utils/subAccounts/subAccountsTableSettings.tsx'
import Alert from '../../common/Alert/Alert.tsx'
import SelectInputs, {
  MultiOption
} from '../../common/forms/selectInputs/SelectInputs.tsx'
import Loader from '../../common/Loader/Loader.tsx'
import Table from '../../common/Table/Table.tsx'
import SearchAccount from '../SearchAccount'

const SubAccountsList = ({
  newAccountCreated
}: {
  newAccountCreated: string
}) => {
  const { state } = useAuth()

  const [allAccs, setAllAccs] = useState<SubAccount[] | null>(null)
  const [searchAccounts, setSearchAccounts] = useState<string[] | null>(null)
  const [subAccountsList, setSubAccountsList] = useState<SubAccount[] | null>(
    []
  )
  const [selectedInputsValues, setSelectedInputsValues] = useState<string[]>([
    '!pd3_users',
    'pd3_users'
  ])
  const hasInitializedFiltersRef = useRef(false)

  const multiOptions: MultiOption[] = [
    {
      value: 'pd3_users',
      text: 'Dietetycy',
      field: '',
      selected: false
    },
    {
      value: '!pd3_users',
      text: 'Inni',
      field: '',
      selected: false
    }
  ]

  const fetchData = async () => {
    const response = await ddPanelMainAccountAPI.getSubAccounts()
    if (response.status === 200) {
      return response.data.sort((accA: SubAccount, accB: SubAccount) =>
        accA.user_email.localeCompare(accB.user_email)
      )
    }
  }

  const { data, isLoading, isError } = useQuery({
    queryKey: ['subAccounts', newAccountCreated, state.email],
    queryFn: fetchData,
    refetchInterval: 5 * 60 * 1000, // minutes * seconds * milliseconds
    staleTime: 30000 // The data will be considered fresh for X seconds
  })

  useEffect(() => {
    if (data) {
      if (!hasInitializedFiltersRef.current) {
        hasInitializedFiltersRef.current = true
      }
      if (!allAccs) setAllAccs(data)
      setSubAccountsList(data)
    }
  }, [data])

  useEffect(() => {
    if (data) {
      const filtered = data.filter((acc) => {
        if (
          selectedInputsValues.includes('pd3_users') &&
          selectedInputsValues.includes('!pd3_users')
        ) {
          return true
        } else if (
          selectedInputsValues.includes('pd3_users') &&
          !selectedInputsValues.includes('!pd3_users')
        ) {
          return acc.user_groups.includes('pd3_users')
        } else if (
          !selectedInputsValues.includes('pd3_users') &&
          selectedInputsValues.includes('!pd3_users')
        ) {
          return !acc.user_groups.includes('pd3_users')
        } else if (
          !selectedInputsValues.includes('pd3_users') &&
          !selectedInputsValues.includes('!pd3_users')
        ) {
          return false
        }
      })
      setSubAccountsList(
        filtered.filter((acc) =>
          searchAccounts ? searchAccounts.includes(acc.user_email) : true
        )
      )
    }
  }, [selectedInputsValues, data, searchAccounts])

  if (isError) {
    return <div>Wystąpił błąd podczas zapytania na serwer</div>
  }

  const columns = setSubAccountsTableColumns(allAccs)

  const getFilteredAccounsBySearch = (searchAccs: string[] | null) => {
    setSearchAccounts(searchAccs)
  }

  return (
    <>
      <div className="rounded-sm border border-stroke bg-white px-5 pt-6 pb-2.5 shadow-default dark:border-strokedark dark:bg-boxdark sm:px-7.5 xl:pb-1">
        {isLoading && <Loader label={'Pobieranie danych...'} />}
        {subAccountsList && (
          <>
            <div className="flex items-center gap-10 my-2">
              <SelectInputs
                key={JSON.stringify(selectedInputsValues)}
                label={'Filtruj wg typu użytkownika'}
                startWhenEmptyValue={'Wybierz typ konta'}
                multiOptions={multiOptions}
                defaultSelected={selectedInputsValues}
                selectedInputsValues={selectedInputsValues}
                setSelectedInputsValues={setSelectedInputsValues}
              />
              <SearchAccount
                allAccounts={allAccs}
                getFilteredAccounts={getFilteredAccounsBySearch}
              />
            </div>
            <div className="w-full max-h-[600px] overflow-auto">
              <Table
                key={JSON.stringify(subAccountsList)}
                columns={columns}
                data={subAccountsList}
              />
            </div>
          </>
        )}

        {!isLoading && data && data.length === 0 && (
          <div className="m-12 mx-0">
            <Alert
              type="warning"
              message="Brak utworzonych subkont dla tego konta"
              icon={<BiQrScan className="text-lg" />}
            />
          </div>
        )}
      </div>
    </>
  )
}

export default SubAccountsList
